/**
 * Bulk Selection Store
 *
 * Manages the state of bulk selected items across list views.
 * Provides methods for selecting/deselecting items and tracking selection state.
 */

export type SelectionChangeHandler = (selectedIds: Set<string>) => void;

class BulkSelectionStore {
  private selectedIds: Set<string> = new Set();
  private listeners: Set<SelectionChangeHandler> = new Set();
  private allIds: string[] = [];

  /**
   * Subscribe to selection changes
   */
  subscribe(handler: SelectionChangeHandler): () => void {
    this.listeners.add(handler);
    // Notify immediately with current state
    handler(new Set(this.selectedIds));

    // Return unsubscribe function
    return () => {
      this.listeners.delete(handler);
    };
  }

  /**
   * Get current selection state
   */
  getSelectedIds(): Set<string> {
    return new Set(this.selectedIds);
  }

  /**
   * Set all available IDs for select all functionality
   */
  setAllIds(ids: string[]): void {
    this.allIds = ids;
  }

  /**
   * Check if an item is selected
   */
  isSelected(id: string): boolean {
    return this.selectedIds.has(id);
  }

  /**
   * Check if all items are selected
   */
  isAllSelected(): boolean {
    return this.allIds.length > 0 && this.allIds.every(id => this.selectedIds.has(id));
  }

  /**
   * Check if some (but not all) items are selected
   */
  isSomeSelected(): boolean {
    if (this.allIds.length === 0) return false;
    const selectedCount = this.allIds.filter(id => this.selectedIds.has(id)).length;
    return selectedCount > 0 && selectedCount < this.allIds.length;
  }

  /**
   * Toggle selection of a single item
   */
  toggleSelection(id: string): void {
    if (this.selectedIds.has(id)) {
      this.selectedIds.delete(id);
    } else {
      this.selectedIds.add(id);
    }
    this.notifyListeners();
  }

  /**
   * Select multiple items
   */
  selectItems(ids: string[]): void {
    ids.forEach(id => this.selectedIds.add(id));
    this.notifyListeners();
  }

  /**
   * Deselect multiple items
   */
  deselectItems(ids: string[]): void {
    ids.forEach(id => this.selectedIds.delete(id));
    this.notifyListeners();
  }

  /**
   * Select all items
   */
  selectAll(): void {
    this.allIds.forEach(id => this.selectedIds.add(id));
    this.notifyListeners();
  }

  /**
   * Deselect all items
   */
  deselectAll(): void {
    this.selectedIds.clear();
    this.notifyListeners();
  }

  /**
   * Toggle all selection (select all if not all selected, otherwise deselect all)
   */
  toggleAll(): void {
    if (this.isAllSelected()) {
      this.deselectAll();
    } else {
      this.selectAll();
    }
  }

  /**
   * Clear selection and reset state
   */
  reset(): void {
    this.selectedIds.clear();
    this.allIds = [];
    this.notifyListeners();
  }

  /**
   * Get count of selected items
   */
  getSelectedCount(): number {
    return this.selectedIds.size;
  }

  /**
   * Notify all listeners of selection changes
   */
  private notifyListeners(): void {
    this.listeners.forEach(listener => {
      listener(new Set(this.selectedIds));
    });
  }
}

// Export singleton instance
export const bulkSelectionStore = new BulkSelectionStore();